import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { PageHeader, Card, StatTile, Btn, Modal, Field, Input, Select, Badge, EmptyState } from "../components/kr";
import { DatePicker } from "../components/DatePicker";
import { MoneyInput } from "../components/MoneyInput";
import { useConfirm } from "../components/ConfirmDialog";
import { toast } from "sonner";
import { Plus, Trash, CurrencyInr, Bank, TrendUp, TrendDown, Users, House, Lightning, CreditCard, Package } from "@phosphor-icons/react";

const CAT_TONE = { salary: "green", subscription: "orange", loan_interest: "red", fixed: "yellow", payable: "zinc" };

export default function CompanyExpenses() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = searchParams.get("tab") || "overview";
  const [data, setData] = useState(null);
  const [upcoming, setUpcoming] = useState(null);
  const [months, setMonths] = useState(3);
  const [capOpen, setCapOpen] = useState(false);
  const [tab, setTab] = useState(initialTab);
  const { confirm } = useConfirm();

  // Keep URL & tab state in sync (both directions)
  useEffect(() => {
    const urlTab = searchParams.get("tab");
    if (urlTab && urlTab !== tab) setTab(urlTab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const switchTab = (k) => {
    setTab(k);
    const next = new URLSearchParams(searchParams);
    if (k === "overview") next.delete("tab"); else next.set("tab", k);
    setSearchParams(next, { replace: true });
  };

  const load = async () => {
    const [r, u] = await Promise.all([
      api.get("/expense-sheet"),
      api.get("/upcoming-expenses", { params: { months } }),
    ]);
    setData(r.data);
    setUpcoming(u.data);
  };
  useEffect(() => { load(); }, [months]);

  // Aggregate accounting entries by party (must be BEFORE any early return)
  const byParty = useMemo(() => {
    if (!data) return [];
    const m = new Map();
    for (const e of data.accounting_entries || []) {
      const k = e.party || "Unknown";
      if (!m.has(k)) m.set(k, { party: k, paid: 0, pending: 0, entries: [] });
      const g = m.get(k);
      g.entries.push(e);
      if (e.entry_type === "paid" || e.status === "cleared") g.paid += e.amount || 0;
      else if (e.entry_type === "payable") g.pending += e.amount || 0;
    }
    return [...m.values()].sort((a, b) => (b.paid + b.pending) - (a.paid + a.pending));
  }, [data]);

  // Categorize the monthly burn: Salaries · Rent · Utilities · Loan EMIs · Subscriptions · Misc
  const burn = useMemo(() => {
    if (!data) return null;
    const t = data.totals || {};
    const RENT_RX = /\brent\b/i;
    const UTIL_RX = /(electric|water|internet|wifi|utilit|gas|phone|telephon|broadband)/i;
    const fixed = data.fixed || [];
    let rent = 0, util = 0, misc = 0;
    const rentItems = [], utilItems = [], miscItems = [];
    for (const f of fixed) {
      const amt = Number(f.amount || 0);
      if (!amt) continue;
      const name = f.name || "";
      if (RENT_RX.test(name)) { rent += amt; rentItems.push(f); }
      else if (UTIL_RX.test(name)) { util += amt; utilItems.push(f); }
      else { misc += amt; miscItems.push(f); }
    }
    const activeEmployees = (data.employees_count || 0);
    return {
      salaries: t.salaries_monthly || 0,
      salariesCount: activeEmployees,
      rent, rentItems,
      utilities: util, utilItems,
      loans: t.loan_interest_monthly || 0,
      loansItems: data.loans || [],
      subs: t.subscriptions_monthly || 0,
      subsItems: data.subscriptions || [],
      misc, miscItems,
      total: (t.salaries_monthly || 0) + rent + util + (t.loan_interest_monthly || 0) + (t.subscriptions_monthly || 0) + misc,
    };
  }, [data]);

  const delCapital = async (c) => {
    const ok = await confirm({
      title: "Delete capital entry?",
      message: `${c.source} · ₹${c.amount.toLocaleString("en-IN")} on ${c.date}`,
      tone: "danger",
    });
    if (!ok) return;
    await api.delete(`/capital/${c.id}`);
    load();
  };

  if (!data) return <div className="p-10 text-zinc-500">Loading…</div>;

  const t = data.totals;

  return (
    <div className="p-6 lg:p-10">
      <PageHeader
        label="ADMIN ONLY"
        title="Company Expense Sheet"
        subtitle="Every rupee spent by the business — capital injections, purchases, salaries, subscriptions, fixed costs, and outstanding payables."
        action={
          <div className="flex gap-2">
            <Btn variant="secondary" onClick={() => setCapOpen(true)} data-testid="add-capital">
              <Plus size={14} /> Log Capital
            </Btn>
          </div>
        }
      />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <StatTile testId="es-revenue" label="Revenue (all time)" value={`₹${t.revenue.toLocaleString("en-IN")}`} accent="text-green-700" sub={`Cash received: ₹${t.cash_received.toLocaleString("en-IN")}`} />
        <StatTile testId="es-capital" label="Capital Injected" value={`₹${t.total_capital_injected.toLocaleString("en-IN")}`} accent="text-blue-700" sub="Owner / loans / investors" />
        <StatTile testId="es-paid" label="Total Paid" value={`₹${t.total_paid.toLocaleString("en-IN")}`} accent="text-red-700" sub="All cleared payables + paid" />
        <StatTile testId="es-pending" label="Outstanding" value={`₹${t.total_pending.toLocaleString("en-IN")}`} accent="text-orange-700" sub="Pending payables" />
      </div>

      <div className="mb-6 border border-zinc-200 bg-white" data-testid="burn-breakdown">
        <div className="px-4 py-3 border-b border-zinc-200 flex items-center justify-between flex-wrap gap-2">
          <div>
            <div className="kr-label">Monthly Burn Breakdown</div>
            <div className="text-[11px] font-mono text-zinc-500">
              At-a-glance recurring outflow · categorized
            </div>
          </div>
          <div className="text-right">
            <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">
              Total burn / month
            </div>
            <div className="font-mono font-black text-2xl text-red-700" data-testid="burn-total">
              ₹{Math.round(burn?.total || 0).toLocaleString("en-IN")}
            </div>
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6">
          <BurnCard testId="burn-salaries" label="Salaries" value={burn?.salaries || 0} sub={`${burn?.salariesCount || 0} active`} icon={Users} accent="bg-emerald-50 text-emerald-800 border-emerald-300" />
          <BurnCard testId="burn-rent" label="Rent" value={burn?.rent || 0} sub={`${burn?.rentItems?.length || 0} item${(burn?.rentItems?.length || 0) === 1 ? "" : "s"}`} icon={House} accent="bg-blue-50 text-blue-800 border-blue-300" />
          <BurnCard testId="burn-utilities" label="Utilities" value={burn?.utilities || 0} sub={`${burn?.utilItems?.length || 0} item${(burn?.utilItems?.length || 0) === 1 ? "" : "s"}`} icon={Lightning} accent="bg-yellow-50 text-yellow-800 border-yellow-300" />
          <BurnCard testId="burn-loans" label="Loan EMIs" value={burn?.loans || 0} sub={`${burn?.loansItems?.length || 0} loan${(burn?.loansItems?.length || 0) === 1 ? "" : "s"}`} icon={Bank} accent="bg-red-50 text-red-800 border-red-300" />
          <BurnCard testId="burn-subs" label="Subscriptions" value={burn?.subs || 0} sub={`${burn?.subsItems?.length || 0} active`} icon={CreditCard} accent="bg-orange-50 text-orange-800 border-orange-300" />
          <BurnCard testId="burn-misc" label="Misc" value={burn?.misc || 0} sub={`${burn?.miscItems?.length || 0} item${(burn?.miscItems?.length || 0) === 1 ? "" : "s"}`} icon={Package} accent="bg-zinc-50 text-zinc-800 border-zinc-300" />
        </div>
      </div>

      {/* Tabs */}
      <div className="mb-4 flex flex-wrap items-center gap-2 border-b border-zinc-200">
        {[
          { k: "overview", label: "By Party" },
          { k: "upcoming", label: "Upcoming Expenses" },
          { k: "capital", label: `Capital & Loans (${(data.capital || []).length})` },
          { k: "subs", label: `Subscriptions (${(data.subscriptions || []).length})` },
          { k: "fixed", label: `Fixed Costs (${(data.fixed || []).length})` },
        ].map(({ k, label }) => (
          <button
            key={k}
            onClick={() => switchTab(k)}
            data-testid={`es-tab-${k}`}
            className={`px-4 py-2 text-xs font-bold uppercase tracking-widest border-b-4 -mb-px transition ${tab === k ? "text-orange-700 border-orange-600" : "text-zinc-500 border-transparent hover:text-zinc-800"}`}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === "overview" && (
        <Card className="!p-0">
          {byParty.length === 0 ? (
            <div className="p-6"><EmptyState title="No expenses recorded yet" /></div>
          ) : (
            <table className="kr-table">
              <thead><tr><th>Party</th><th>Paid</th><th>Pending</th><th>Total</th><th>Entries</th></tr></thead>
              <tbody>
                {byParty.map(g => (
                  <tr key={g.party}>
                    <td className="font-bold">{g.party}</td>
                    <td className="font-mono text-green-700">₹{g.paid.toLocaleString("en-IN")}</td>
                    <td className="font-mono text-orange-700">₹{g.pending.toLocaleString("en-IN")}</td>
                    <td className="font-mono font-black">₹{(g.paid + g.pending).toLocaleString("en-IN")}</td>
                    <td className="text-xs text-zinc-500">{g.entries.length}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>
      )}

      {tab === "upcoming" && (
        <Card className="!p-0">
          <div className="p-4 flex items-center justify-between border-b border-zinc-200">
            <div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">Forecasted outflows</div>
              <div className="font-black text-2xl mt-1">₹{(upcoming?.total || 0).toLocaleString("en-IN")}</div>
              <div className="text-[11px] text-zinc-500 font-mono">next {months} month{months > 1 ? "s" : ""} · {upcoming?.items?.length || 0} entries</div>
            </div>
            <div className="flex items-center gap-1">
              {[1, 3, 6, 12].map(m => (
                <button
                  key={m}
                  onClick={() => setMonths(m)}
                  className={`px-3 py-1.5 text-[11px] font-bold uppercase tracking-widest border ${months === m ? "bg-zinc-900 text-white border-zinc-900" : "bg-white text-zinc-600 border-zinc-300 hover:border-orange-500"}`}
                >
                  {m}M
                </button>
              ))}
            </div>
          </div>
          {upcoming?.by_category && Object.keys(upcoming.by_category).length > 0 && (
            <div className="p-4 border-b border-zinc-200 flex flex-wrap gap-4">
              {Object.entries(upcoming.by_category).map(([k, v]) => (
                <div key={k} className="border-l-4 border-orange-500 pl-3">
                  <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">{k}</div>
                  <div className="font-mono font-bold">₹{v.toLocaleString("en-IN")}</div>
                </div>
              ))}
            </div>
          )}
          {(upcoming?.items || []).length === 0 ? (
            <div className="p-6"><EmptyState title="Nothing forecasted" hint="Add salaries, fixed costs, subscriptions and loans to see them here." /></div>
          ) : (
            <table className="kr-table">
              <thead><tr><th>Due</th><th>Category</th><th>Description</th><th className="text-right">Amount</th></tr></thead>
              <tbody>
                {upcoming.items.map((it, idx) => (
                  <tr key={idx}>
                    <td className="font-mono text-xs">{it.date}</td>
                    <td><Badge tone={CAT_TONE[it.category] || "zinc"}>{it.category}</Badge></td>
                    <td className="text-sm">{it.description}</td>
                    <td className="font-mono font-bold text-right">₹{it.amount.toLocaleString("en-IN")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>
      )}

      {tab === "capital" && (
        <Card className="!p-0">
          {(data.capital || []).length === 0 ? (
            <div className="p-6"><EmptyState title="No capital / loan entries yet" hint='Click "Log Capital" to record the initial loan, investment or owner contribution.' /></div>
          ) : (
            <table className="kr-table">
              <thead><tr><th>Date</th><th>Source</th><th>Type</th><th>Amount</th><th>Interest</th><th>Monthly</th><th>Notes</th><th></th></tr></thead>
              <tbody>
                {data.capital.map(c => {
                  const monthly = c.emi_amount > 0
                    ? c.emi_amount
                    : (c.is_loan ? (c.amount * (c.interest_rate_percent || 0) / 100.0) / 12.0 : 0);
                  return (
                    <tr key={c.id}>
                      <td className="font-mono text-xs">{c.date}</td>
                      <td className="font-bold">
                        {c.source}
                        {c.income_source && <div className="text-[10px] text-zinc-500 font-mono">{c.income_source}</div>}
                      </td>
                      <td>
                        {c.is_loan
                          ? <Badge tone="red">loan</Badge>
                          : <Badge tone="blue">capital</Badge>}
                      </td>
                      <td className="font-mono font-bold text-blue-700">₹{c.amount.toLocaleString("en-IN")}</td>
                      <td className="font-mono text-xs">
                        {c.is_loan ? `${c.interest_rate_percent || 0}% p.a.` : "—"}
                        {c.loan_end_date && <div className="text-[10px] text-zinc-500">till {c.loan_end_date}</div>}
                      </td>
                      <td className="font-mono font-bold text-orange-700">
                        {monthly > 0 ? `₹${Math.round(monthly).toLocaleString("en-IN")}` : "—"}
                      </td>
                      <td className="text-xs text-zinc-600 max-w-xs">{c.notes}</td>
                      <td><button onClick={() => delCapital(c)} className="text-red-600"><Trash size={14} weight="bold" /></button></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </Card>
      )}

      {tab === "subs" && (
        <Card className="!p-0">
          {data.subscriptions.length === 0 ? (
            <div className="p-6"><EmptyState title="No subscriptions" /></div>
          ) : (
            <table className="kr-table">
              <thead><tr><th>Service</th><th>Cycle</th><th>Amount</th><th>Monthly Equivalent</th></tr></thead>
              <tbody>
                {data.subscriptions.map((s, idx) => (
                  <tr key={idx}>
                    <td className="font-bold">{s.name}</td>
                    <td><Badge tone="zinc">{s.cycle}</Badge></td>
                    <td className="font-mono">₹{s.amount.toLocaleString("en-IN")}</td>
                    <td className="font-mono font-bold">₹{s.monthly_equivalent.toLocaleString("en-IN")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>
      )}

      {tab === "fixed" && (
        <Card className="!p-0">
          {data.fixed.length === 0 ? (
            <div className="p-6"><EmptyState title="No fixed monthly expenses set" hint="Add these in Settings → Fixed Monthly Expenses." /></div>
          ) : (
            <table className="kr-table">
              <thead><tr><th>Item</th><th>Amount / month</th></tr></thead>
              <tbody>
                {data.fixed.map((s, idx) => (
                  <tr key={idx}>
                    <td className="font-bold">{s.name}</td>
                    <td className="font-mono font-bold">₹{(s.amount || 0).toLocaleString("en-IN")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>
      )}

      {capOpen && (
        <CapitalModal onClose={() => setCapOpen(false)} onSaved={() => { setCapOpen(false); load(); }} />
      )}
    </div>
  );
}

function BurnCard({ label, value, sub, icon: Icon, accent, testId }) {
  return (
    <div className={`p-4 border-r border-b last:border-r-0 md:[&:nth-child(3n)]:border-r-0 lg:[&:nth-child(6n)]:border-r-0 lg:[&:nth-child(3n)]:border-r ${accent || "border-zinc-200 bg-white"}`} data-testid={testId}>
      <div className="flex items-start justify-between mb-2">
        <div className="text-[10px] font-mono uppercase tracking-widest opacity-80">{label}</div>
        {Icon && <Icon size={16} weight="bold" className="opacity-60" />}
      </div>
      <div className="font-mono font-black text-xl leading-tight">
        ₹{Math.round(Number(value || 0)).toLocaleString("en-IN")}
      </div>
      {sub && <div className="text-[10px] font-mono opacity-70 mt-1">{sub}</div>}
    </div>
  );
}

function MiniStat({ label, value, accent = "text-zinc-900" }) {
  return (
    <div className="bg-white border border-zinc-200 p-4">
      <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">{label}</div>
      <div className={`font-mono font-black text-xl mt-1 ${accent}`}>₹{Number(value || 0).toLocaleString("en-IN")}</div>
    </div>
  );
}

function CapitalModal({ onClose, onSaved }) {
  const [sources, setSources] = React.useState([]);
  React.useEffect(() => {
    api.get("/settings").then(r => setSources(r.data.income_sources || [])).catch(() => {});
  }, []);
  const [f, setF] = useState({
    date: new Date().toISOString().slice(0, 10),
    amount: "",
    source: "Owner",
    income_source: "Owner Contribution",
    notes: "",
    is_loan: false,
    interest_rate_percent: "",
    interest_frequency: "monthly",
    emi_amount: "",
    loan_end_date: "",
    moratorium_months: "",
    moratorium_amount: "",
  });
  const upd = (k, v) => setF({ ...f, [k]: v });
  const submit = async (e) => {
    e.preventDefault();
    if (f.is_loan && !f.loan_end_date) {
      toast.error("Loan end date is required — it drives the EMI and payoff schedule.");
      return;
    }
    if (f.is_loan && f.loan_end_date && f.date && f.loan_end_date <= f.date) {
      toast.error("Loan end date must be AFTER the start date.");
      return;
    }
    try {
      const body = {
        ...f,
        amount: parseFloat(f.amount),
        interest_rate_percent: parseFloat(f.interest_rate_percent || 0),
        emi_amount: parseFloat(f.emi_amount || 0),
        moratorium_months: parseInt(f.moratorium_months || 0, 10) || 0,
        moratorium_amount: parseFloat(f.moratorium_amount || 0) || 0,
      };
      await api.post("/capital", body);
      toast.success("Capital entry logged");
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  // ---------------- Loan preview math (mirrors backend _compute_schedule) ----
  const principal = parseFloat(f.amount) || 0;
  const annualPct = parseFloat(f.interest_rate_percent) || 0;
  const r = annualPct / 100 / 12;                     // monthly rate
  const morMonths = parseInt(f.moratorium_months || 0, 10) || 0;
  const morAmtInput = parseFloat(f.moratorium_amount || 0) || 0;
  const fixedEmi = parseFloat(f.emi_amount || 0) || 0;

  // Tenure from dates (months)
  let tenureMonths = 0;
  if (f.date && f.loan_end_date) {
    try {
      const s = new Date(f.date);
      const e = new Date(f.loan_end_date);
      tenureMonths = Math.max(
        0,
        (e.getFullYear() - s.getFullYear()) * 12 + (e.getMonth() - s.getMonth())
      );
    } catch { tenureMonths = 0; }
  }
  const payingMonths = Math.max(1, tenureMonths - morMonths);

  // Standard EMI (post-moratorium)
  let computedEmi = 0;
  if (fixedEmi > 0) {
    computedEmi = fixedEmi;
  } else if (principal > 0 && payingMonths > 0) {
    if (r > 0) {
      computedEmi = (principal * r * Math.pow(1 + r, payingMonths))
                    / (Math.pow(1 + r, payingMonths) - 1);
    } else {
      computedEmi = principal / payingMonths;
    }
  }
  const monthlyPreview = f.is_loan ? computedEmi : 0;

  // Moratorium: interest-only against original principal
  const morInterestOnly = principal * r;
  const morPreview = morMonths > 0 ? (morAmtInput > 0 ? morAmtInput : morInterestOnly) : 0;

  // Total interest / payoff sanity
  const totalPay = monthlyPreview * payingMonths + morPreview * morMonths;
  const totalInterest = Math.max(0, totalPay - principal);
  return (
    <Modal open onClose={onClose} title="Log Capital Injection / Loan" size="lg">
      <form onSubmit={submit} className="grid grid-cols-2 gap-4" data-testid="capital-form">
        <Field label="Start date"><DatePicker value={f.date} onChange={e => upd("date", e.target.value)} required /></Field>
        <Field label="Amount"><MoneyInput value={f.amount} onChange={e => upd("amount", e.target.value)} min={0} required placeholder="₹0.00" className="w-full px-3 py-2 border border-zinc-300 text-sm font-mono" /></Field>
        <Field label="Source label" full>
          <Input value={f.source} onChange={e => upd("source", e.target.value)} placeholder="e.g. HDFC Bank Loan · Ram (Investor) · Owner Kishore" required data-testid="capital-source" />
        </Field>
        <Field label="Income category (from settings)">
          <Select value={f.income_source} onChange={e => upd("income_source", e.target.value)} data-testid="capital-income-src">
            <option value="">— select —</option>
            {sources.map(s => <option key={s} value={s}>{s}</option>)}
          </Select>
        </Field>
        <Field label="Is this a loan (with interest)?">
          <Select value={f.is_loan ? "yes" : "no"} onChange={(e) => upd("is_loan", e.target.value === "yes")} data-testid="capital-is-loan">
            <option value="no">No · one-time capital / investment</option>
            <option value="yes">Yes · monthly interest to be paid</option>
          </Select>
        </Field>
        {f.is_loan && (
          <>
            <Field label="Interest rate (annual %)">
              <Input type="number" step="0.01" value={f.interest_rate_percent} onChange={e => upd("interest_rate_percent", e.target.value)} data-testid="capital-rate" placeholder="e.g. 9.5 for 9.5% p.a." required />
            </Field>
            <Field label="Loan end date (required · drives EMI)">
              <DatePicker value={f.loan_end_date} onChange={e => upd("loan_end_date", e.target.value)} data-testid="capital-end" required />
            </Field>
            <Field label="Moratorium months (grace period · 0 = none)">
              <Input type="number" min="0" step="1" value={f.moratorium_months}
                     onChange={e => upd("moratorium_months", e.target.value)}
                     data-testid="capital-mor-months"
                     placeholder="e.g. 6 (interest-only for 6 months)" />
            </Field>
            <Field label="Fixed EMI (optional · overrides auto-calc)">
              <MoneyInput value={f.emi_amount} onChange={e => upd("emi_amount", e.target.value)} min={0} placeholder={computedEmi > 0 ? `Auto: ₹${computedEmi.toLocaleString("en-IN", { maximumFractionDigits: 2 })}` : "Fill dates + rate first"} className="w-full px-3 py-2 border border-zinc-300 text-sm font-mono" data-testid="capital-emi" />
            </Field>
            <Field label="Moratorium monthly payment (blank = interest-only auto)" full>
              <MoneyInput value={f.moratorium_amount}
                          onChange={e => upd("moratorium_amount", e.target.value)}
                          min={0}
                          placeholder={morInterestOnly > 0 ? `Auto: ₹${morInterestOnly.toLocaleString("en-IN", { maximumFractionDigits: 2 })}` : "Leave blank for interest-only"}
                          className="w-full px-3 py-2 border border-zinc-300 text-sm font-mono"
                          data-testid="capital-mor-amount" />
            </Field>
            <div className="col-span-2 bg-orange-50 border-l-4 border-orange-500 p-3 text-xs" data-testid="loan-preview">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-zinc-700">
                <div>
                  <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">Tenure</div>
                  <div className="font-mono font-black text-sm">
                    {tenureMonths} mo
                    {tenureMonths > 0 && morMonths > 0 && (
                      <span className="text-zinc-500 font-normal">
                        {" "}(≡ {morMonths} mor + {payingMonths} paying)
                      </span>
                    )}
                  </div>
                </div>
                <div>
                  <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">Monthly EMI</div>
                  <div className="font-mono font-black text-sm text-orange-700">
                    ₹{monthlyPreview.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                  </div>
                </div>
                <div>
                  <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">Moratorium EMI</div>
                  <div className="font-mono font-black text-sm text-red-700">
                    {morMonths > 0
                      ? `₹${morPreview.toLocaleString(undefined, { maximumFractionDigits: 2 })}`
                      : "—"}
                  </div>
                </div>
                <div>
                  <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">Total interest</div>
                  <div className="font-mono font-black text-sm text-zinc-800">
                    ₹{totalInterest.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                  </div>
                </div>
              </div>
              <div className="text-zinc-600 mt-3 border-t border-orange-200 pt-2">
                Auto-calculated from <b>Start → End</b>, <b>Amount</b>, <b>Rate</b> and <b>Moratorium</b>.
                {fixedEmi > 0 && <> Currently overridden by the manual "Fixed EMI" value above.</>}
                {tenureMonths <= 0 && <> Enter both dates to compute.</>}
              </div>
              {morMonths > 0 && morMonths >= tenureMonths && tenureMonths > 0 && (
                <div className="mt-2 text-red-700 font-semibold">
                  Moratorium ({morMonths}mo) must be shorter than total tenure ({tenureMonths}mo).
                </div>
              )}
            </div>
          </>
        )}
        <Field label="Notes" full>
          <Input value={f.notes} onChange={e => upd("notes", e.target.value)} placeholder="e.g. Initial setup loan from HDFC Bank at 9% interest, 5-year term" />
        </Field>
        <div className="col-span-2 flex justify-end gap-2">
          <Btn type="button" variant="secondary" onClick={onClose}>Cancel</Btn>
          <Btn type="submit">{f.is_loan ? "Log Loan" : "Log Capital"}</Btn>
        </div>
      </form>
    </Modal>
  );
}
